import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useModuleProgress from '../hooks/useModuleProgress';
import '../styles/pages/_module-pages.scss';
import '../styles/pages/_learn-assessments.scss';
import '../styles/pages/_quiz-completion.scss';

interface QuizQuestion {
  question: string;
  options: string[];
  correctAnswer: number;
  explanation: string;
}

const quizQuestions: QuizQuestion[] = [
  {
    question: "Negative self-talk patterns like 'I'm not good enough' typically originate from:",
    options: [
      "Your actual abilities and performance",
      "Past experiences, criticism, or internalized messages",
      "Your current mood that day",
      "Other people's thoughts about you"
    ],
    correctAnswer: 1,
    explanation: "Negative self-talk usually develops from past experiences, feedback we've received, and messages we've internalized–not from objective truth about our abilities."
  },
  {
    question: "Which response to a mistake demonstrates healthy self-esteem?",
    options: [
      "Ignoring the mistake completely",
      "Believing you're fundamentally incapable",
      "Acknowledging the mistake, learning from it, and moving forward",
      "Criticizing yourself harshly to motivate improvement"
    ],
    correctAnswer: 2,
    explanation: "Healthy self-esteem means treating yourself with the same compassion and fairness you'd offer a good friend. Mistakes are opportunities to learn, not proof of failure."
  },
  {
    question: "Self-compassion is NOT about:",
    options: [
      "Treating yourself kindly when struggling",
      "Recognizing that difficulty is part of being human",
      "Avoiding all responsibility and accountability",
      "Understanding yourself with the same perspective you'd offer others"
    ],
    correctAnswer: 2,
    explanation: "Self-compassion includes healthy accountability. It means being kind to yourself while still taking responsibility for growth and positive change."
  },
  {
    question: "To build confidence, it's most effective to:",
    options: [
      "Wait until you feel confident before taking action",
      "Only attempt tasks you're already excellent at",
      "Take small actions aligned with your values and build from there",
      "Compare yourself regularly to others to identify gaps"
    ],
    correctAnswer: 2,
    explanation: "Confidence builds through action and experience. Small wins compound, creating momentum and reinforcing your capability."
  },
  {
    question: "When you notice negative self-talk, a helpful first step is to:",
    options: [
      "Try to force positive thoughts immediately",
      "Ignore it and hope it goes away",
      "Observe the thought without judgment and gently question its validity",
      "Believe it completely and feel worse"
    ],
    correctAnswer: 2,
    explanation: "Observation without judgment creates space to respond differently. You can then evaluate whether the thought is actually true or helpful."
  }
];

const EmotioPsychoRationalModule3QuizPage: React.FC = () => {
  const navigate = useNavigate();
  const { currentStep, setCurrentStep, isLoading, saveProgress } = useModuleProgress('emotioPsychoRationalM3');
  const [currentQuestion, setCurrentQuestion] = useState<number>(0);
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
  const [showExplanation, setShowExplanation] = useState(false);
  const [responses, setResponses] = useState<{[key: number]: number}>({});
  const [showCompletion, setShowCompletion] = useState(false);

  const handleAnswerSelect = (answerIndex: number) => {
    if (responses[currentQuestion] === undefined) {
      setSelectedAnswer(answerIndex);
      setShowExplanation(true);
      setResponses(prev => ({...prev, [currentQuestion]: answerIndex}));
    }
  };

  const handleNext = () => {
    if (currentQuestion < quizQuestions.length - 1) {
      const nextQuestion = currentQuestion + 1;
      setCurrentQuestion(nextQuestion);
      setSelectedAnswer(null);
      setShowExplanation(false);
      saveProgress(nextQuestion + 1);
    } else {
      saveProgress(quizQuestions.length, true);
      setShowCompletion(true);
    }
  };

  const handleBack = () => {
    if (currentQuestion > 0) {
      const prevQuestion = currentQuestion - 1;
      const previousResponse = responses[prevQuestion];
      setCurrentQuestion(prevQuestion);
      setSelectedAnswer(previousResponse ?? null);
      setShowExplanation(previousResponse !== undefined);
      saveProgress(prevQuestion + 1);
    }
  };

  const handleExit = () => {
    navigate('/panel/learn/emotio-psycho-rational-resources');
  };

  if (showCompletion) {
    return (
      <div className="quiz-completion-section">
        <h2 className="completion-title">
          Congratulations!
        </h2>
        <p className="completion-subtitle">
          You've successfully completed the Module 3 quiz!
        </p>

        <div className="completion-card">
          <div className="achievement-section">
            <h3 className="section-title">
              Key Achievements
            </h3>
            <p className="achievement-intro">You've demonstrated a great understanding of:</p>
            <ul className="achievement-list">
              <li>
                The origins of negative self-talk
              </li>
              <li>
                How to respond to mistakes with self-compassion
              </li>
              <li>
                Practical ways to build confidence
              </li>
            </ul>
          </div>

          <div className="next-steps-section">
            <h3 className="section-title">
              Next Steps
            </h3>
            <p>Continue practicing self-compassion and challenging negative self-talk!</p>
            <p>Ready to continue your wellness journey?</p>
          </div>

          <button
            className="resource-button"
            onClick={handleExit}
          >
            Return to Emotio-Psycho-Rational Resources
          </button>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="module-form" style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <p>Loading quiz...</p>
      </div>
    );
  }

  const progress = ((currentQuestion + 1) / quizQuestions.length) * 100;
  const currentQuiz = quizQuestions[currentQuestion];

  return (
    <div className="module-form">
      <div className="module-container">
        <div className="module-left emotio-psycho-rational-panel">
          <div className="module-header">
            <h1>Module 3 Quiz</h1>
            <p className="module-subtitle">Cognitive Behavioral Techniques</p>
            <hr className="module-divider" />
            <p className="module-tool-info">
              Let's review what we've learned about cognitive behavioral techniques.
              There's no scoring - this is just to reinforce the main ideas!
            </p>
          </div>
        </div>

        <div className="assessment-right-panel">
          <div className="assessment-content">
            <div className="module-progress">
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${progress}%` }}></div>
              </div>
              <span className="progress-text">
                Question {currentQuestion + 1} of {quizQuestions.length}
              </span>
            </div>

            <div className="question-section">
              <h2 className="question-title">
                {currentQuiz.question}
              </h2>
              <p className="question-instruction">
                Please select the answer that best matches your understanding.
              </p>

              <div className="answer-options">
                {currentQuiz.options.map((option, index) => (
                  <label key={index} className="answer-option">
                    <input
                      type="radio"
                      name={`question-${currentQuestion}`}
                      value={index}
                      checked={selectedAnswer === index}
                      onChange={() => handleAnswerSelect(index)}
                      disabled={selectedAnswer !== null}
                    />
                    <span className="option-text">{option}</span>
                  </label>
                ))}
              </div>

              {showExplanation && (
                <div className="explanation">
                  <h3>{selectedAnswer === currentQuiz.correctAnswer ? 'Correct!' : 'Remember:'}</h3>
                  <p>{currentQuiz.explanation}</p>
                </div>
              )}
            </div>
          </div>

          <div className="navigation-buttons">
            <button
              className="btn-secondary"
              onClick={currentQuestion === 0 ? handleExit : handleBack}
            >
              {currentQuestion === 0 ? 'Exit Quiz' : 'Previous Question'}
            </button>
            
              {currentQuestion < quizQuestions.length - 1 ? (
                <button
                  className="btn-primary"
                  onClick={handleNext}
                  disabled={selectedAnswer === null}
                >
                  Next Question
                </button>
              ) : (
                <button
                  className="btn-primary"
                  onClick={handleNext}
                  disabled={selectedAnswer === null}
                >
                  Complete Quiz
                </button>
              )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmotioPsychoRationalModule3QuizPage;


